import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import {
  getAllDaysMeta,
  getReferenceSections,
  getUserWins,
  getFrontMatter,
  getSnapshotWithAnswers,
} from "../services/moneyMagicService";
import MicroWinModal from "../components/MicroWinModal";
import "./MoneyMagicHome.css";

const getSessionFromStorage = () => {
  try {
    const storageKey = Object.keys(localStorage).find(
      (k) => k.includes("sb-") && k.includes("-auth-token")
    );
    if (!storageKey) return null;
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return { userId: parsed?.user?.id };
  } catch {
    return null;
  }
};

function UntangleHome({ journal, progress }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [daysMeta, setDaysMeta] = useState([]);
  const [referenceSections, setReferenceSections] = useState([]);
  const [wins, setWins] = useState([]);
  const [expandedRef, setExpandedRef] = useState(null);
  const [showMicroWin, setShowMicroWin] = useState(false);
  const [userId, setUserId] = useState(null);
  const [frontMatter, setFrontMatter] = useState([]);
  const [beforeSnapshot, setBeforeSnapshot] = useState(null);
  const [afterSnapshot, setAfterSnapshot] = useState(null);
  const [expandedFrontMatter, setExpandedFrontMatter] = useState(false);
  const [expandedBeforeSnapshot, setExpandedBeforeSnapshot] = useState(false);
  const [expandedAfterSnapshot, setExpandedAfterSnapshot] = useState(false);

  useEffect(() => {
    if (!journal?.id) return;
    let alive = true;

    const load = async () => {
      const session = getSessionFromStorage();
      if (!session?.userId) return;

      const [meta, refs, userWins, fm, snapBefore, snapAfter] = await Promise.all([
        getAllDaysMeta(journal.id),
        getReferenceSections(journal.id),
        getUserWins(session.userId, journal.id),
        getFrontMatter(journal.id),
        getSnapshotWithAnswers(session.userId, journal.id, "before"),
        getSnapshotWithAnswers(session.userId, journal.id, "after"),
      ]);

      if (!alive) return;

      setUserId(session.userId);
      setDaysMeta(meta);
      setReferenceSections(refs);
      setWins(userWins);
      setFrontMatter(fm || []);
      setBeforeSnapshot(snapBefore);
      setAfterSnapshot(snapAfter);
      setLoading(false);
    };

    load();
    return () => { alive = false; };
  }, [journal]);

  if (loading) {
    return (
      <div className="mmh-gate">
        <p className="mmh-gate-text">Opening your journal…</p>
      </div>
    );
  }

  const totalDays = journal?.total_days ?? 30;
  const currentDay = progress?.currentDay ?? progress?.current_day ?? 1;
  const completedCount = Math.max(0, currentDay - 1);
  const pct = Math.round((completedCount / totalDays) * 100);

  const getDayStatus = (n) => {
    if (n < currentDay) return "completed";
    if (n === currentDay) return "current";
    return "locked";
  };

  return (
    <div className="mmh-page">

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="mmh-header">
        <p className="mmh-eyebrow">Edge &amp; Altar · The Untangle Journal</p>
        <h1 className="mmh-title">Your Journey</h1>

        <div className="mmh-progress-row">
          <div className="mmh-progress-track">
            <div className="mmh-progress-fill" style={{ width: `${pct}%` }} />
          </div>
          <span className="mmh-progress-label">
            {completedCount} of {totalDays} days
          </span>
        </div>

        <button
          className="mmh-continue-btn"
          type="button"
          onClick={() => navigate(`/journal/the-untangle-journal/day/${currentDay}`)}
        >
          Continue Day {currentDay} →
        </button>
      </div>

      {/* ── Day grid ────────────────────────────────────────────────────── */}
      <div className="mmh-grid">
        {Array.from({ length: totalDays }, (_, i) => i + 1).map((n) => {
          const meta = daysMeta.find((d) => d.dayNumber === n);
          const status = getDayStatus(n);

          if (status === "current") {
            return (
              <button
                key={n}
                className="mmh-day mmh-day--current"
                type="button"
                onClick={() => navigate(`/journal/the-untangle-journal/day/${n}`)}
              >
                <span className="mmh-day-num">Day {n}</span>
                {meta?.title && <span className="mmh-day-title">{meta.title}</span>}
                <span className="mmh-day-cta">Today →</span>
              </button>
            );
          }

          if (status === "completed") {
            return (
              <button
                key={n}
                className="mmh-day mmh-day--done"
                type="button"
                onClick={() => navigate(`/journal/the-untangle-journal/day/${n}`)}
              >
                <span className="mmh-day-check" aria-hidden="true">✦</span>
                <span className="mmh-day-num">Day {n}</span>
                {meta?.title && <span className="mmh-day-title">{meta.title}</span>}
              </button>
            );
          }

          return (
            <div key={n} className="mmh-day mmh-day--locked" aria-hidden="true">
              <span className="mmh-day-num">Day {n}</span>
            </div>
          );
        })}
      </div>

      {/* ── Micro-wins ───────────────────────────────────────────────────── */}
      {journal?.has_micro_wins && (
        <div className="mmh-wins-section">
          <div className="mmh-wins-header">
            <h2 className="mmh-section-heading">Micro-Wins</h2>
            <button
              className="mmh-add-win-btn"
              type="button"
              onClick={() => setShowMicroWin(true)}
            >
              ✦ Add a Win
            </button>
          </div>

          {wins.length > 0 ? (
            <ul className="mmh-wins-list">
              {wins.map((w) => (
                <li key={w.id} className="mmh-win-item">
                  <span className="mmh-win-mark" aria-hidden="true">✦</span>
                  {w.winText}
                </li>
              ))}
            </ul>
          ) : (
            <p className="mmh-wins-empty">
              Track the small shifts — moments of clarity, a feeling that lifted, a pattern you noticed.
            </p>
          )}
        </div>
      )}

      {/* ── About This Journal (front matter) ──────────────────────────── */}
      {frontMatter.length > 0 && (
        <div className="mmh-front-matter">
          <div className="mmh-ref-block">
            <button
              className={`mmh-ref-btn ${expandedFrontMatter ? "mmh-ref-btn--open" : ""}`}
              type="button"
              onClick={() => setExpandedFrontMatter((prev) => !prev)}
            >
              <span aria-hidden="true">✦</span>
              About This Journal
              <span className="mmh-chevron" aria-hidden="true">
                {expandedFrontMatter ? "▲" : "▼"}
              </span>
            </button>
            {expandedFrontMatter && (
              <div className="mmh-ref-content mmh-md">
                {frontMatter.map((section) => (
                  <div key={section.id} className="mmh-fm-section">
                    <h3 className="mmh-fm-section-title">{section.sectionTitle}</h3>
                    <ReactMarkdown>{section.content || ""}</ReactMarkdown>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Before Snapshot ─────────────────────────────────────────────── */}
      {beforeSnapshot && beforeSnapshot.length > 0 && (
        <div className="mmh-snapshot-section">
          <div className="mmh-ref-block">
            <button
              className={`mmh-ref-btn ${expandedBeforeSnapshot ? "mmh-ref-btn--open" : ""}`}
              type="button"
              onClick={() => setExpandedBeforeSnapshot((prev) => !prev)}
            >
              <span aria-hidden="true">🌙</span>
              Before Snapshot — Day 1
              <span className="mmh-chevron" aria-hidden="true">
                {expandedBeforeSnapshot ? "▲" : "▼"}
              </span>
            </button>
            {expandedBeforeSnapshot && (
              <div className="mmh-ref-content">
                <div className="mmh-snapshot-qa">
                  {beforeSnapshot.map((item, i) => (
                    <div key={i} className="mmh-snapshot-item">
                      <p className="mmh-snapshot-qnum">Question {i + 1}</p>
                      <p className="mmh-snapshot-q">{item.questionText}</p>
                      <p className="mmh-snapshot-a">
                        {item.response || <span className="mmh-snapshot-empty">No response recorded.</span>}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── After Snapshot ──────────────────────────────────────────────── */}
      {afterSnapshot && afterSnapshot.length > 0 && (
        <div className="mmh-snapshot-section">
          <div className="mmh-ref-block">
            <button
              className={`mmh-ref-btn ${expandedAfterSnapshot ? "mmh-ref-btn--open" : ""}`}
              type="button"
              onClick={() => setExpandedAfterSnapshot((prev) => !prev)}
            >
              <span aria-hidden="true">✦</span>
              After Snapshot — Day 30
              <span className="mmh-chevron" aria-hidden="true">
                {expandedAfterSnapshot ? "▲" : "▼"}
              </span>
            </button>
            {expandedAfterSnapshot && (
              <div className="mmh-ref-content">
                <div className="mmh-snapshot-qa">
                  {afterSnapshot.map((item, i) => (
                    <div key={i} className="mmh-snapshot-item">
                      <p className="mmh-snapshot-qnum">Question {i + 1}</p>
                      <p className="mmh-snapshot-q">{item.questionText}</p>
                      <p className="mmh-snapshot-a">
                        {item.response || <span className="mmh-snapshot-empty">No response recorded.</span>}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Reference sections ──────────────────────────────────────────── */}
      {referenceSections.length > 0 && (
        <div className="mmh-refs">
          <h2 className="mmh-section-heading">Reference</h2>
          {referenceSections.map((section) => (
            <div key={section.id} className="mmh-ref-block">
              <button
                className={`mmh-ref-btn ${expandedRef === section.id ? "mmh-ref-btn--open" : ""}`}
                type="button"
                onClick={() =>
                  setExpandedRef((prev) => (prev === section.id ? null : section.id))
                }
              >
                <span aria-hidden="true">📖</span>
                {section.title}
                <span className="mmh-chevron" aria-hidden="true">
                  {expandedRef === section.id ? "▲" : "▼"}
                </span>
              </button>
              {expandedRef === section.id && (
                <div className="mmh-ref-content mmh-md">
                  <ReactMarkdown>{section.content || ""}</ReactMarkdown>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Also in your library ───────────────────────────────────────── */}
      <div className="mmh-also">
        <div className="mmh-also-card">
          <div>
            <p className="mmh-also-label">Also in your library</p>
            <p className="mmh-also-name">The Money Magic Journal</p>
            <p className="mmh-also-desc">
              30 days of psychology-backed shadow work for your relationship with money.
            </p>
          </div>
          <button
            className="mmh-also-link"
            type="button"
            onClick={() => navigate("/journal/money-magic")}
          >
            Open →
          </button>
        </div>
      </div>

      {journal?.has_micro_wins && showMicroWin && (
        <MicroWinModal
          userId={userId}
          journalId={journal.id}
          dayNumber={currentDay}
          onClose={() => {
            setShowMicroWin(false);
            if (userId && journal?.id) {
              getUserWins(userId, journal.id).then(setWins);
            }
          }}
        />
      )}
    </div>
  );
}

export default UntangleHome;
